import Modals from 'application/ui/modal/modal'
import { refreshFocusLock } from 'application/utils/focus-lock'

const SAVED_STATUS_TIMEOUT = 2000
const NOT_VISITED = '-1'
const ORDER_MODAL = 'lk_order_details'
const VISIT_FIELD = 'is_visited_owner'
const REASON_FIELD = 'cancellation_reason_owner'

export function initPartnerApplication() {
  const form = document.querySelector<HTMLFormElement>(
    '[data-partner-application]'
  )
  const list = form?.querySelector<HTMLElement>('[data-application-quests]')
  const template = form?.querySelector<HTMLTemplateElement>(
    '[data-application-quest-template]'
  )
  const addButton = form?.querySelector<HTMLButtonElement>(
    '[data-add-application-quest]'
  )
  if (!form || !list || !template || !addButton) return

  const visibleCards = () =>
    Array.from(
      list.querySelectorAll<HTMLElement>('[data-application-quest]')
    ).filter((card) => !card.hidden)

  const refresh = () => {
    const cards = visibleCards()
    cards.forEach((card, index) => {
      const title = card.querySelector<HTMLElement>(
        '[data-application-quest-title]'
      )
      const remove = card.querySelector<HTMLButtonElement>(
        '[data-remove-application-quest]'
      )
      if (title) title.textContent = `Квест ${index + 1}`
      if (remove) remove.hidden = false
    })
    addButton.textContent = cards.length
      ? '+ Добавить ещё квест'
      : '+ Добавить квест'
    addButton.hidden = cards.length >= 10
  }

  addButton.addEventListener('click', () => {
    const index = `${Date.now()}${Math.floor(Math.random() * 1000)}`
    list.insertAdjacentHTML(
      'beforeend',
      template.innerHTML.split('NEW_QUEST').join(index)
    )
    refresh()
    const cards = visibleCards()
    cards[cards.length - 1]?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    })
  })

  list.addEventListener('click', (event) => {
    const button = (event.target as HTMLElement).closest<HTMLButtonElement>(
      '[data-remove-application-quest]'
    )
    const card = button?.closest<HTMLElement>('[data-application-quest]')
    if (!button || !card) return

    const destroy = card.querySelector<HTMLInputElement>(
      '[data-application-quest-destroy]'
    )
    if (destroy && card.querySelector('input[name$="[id]"]')) {
      destroy.value = '1'
      card.hidden = true
    } else {
      card.remove()
    }
    refresh()
  })

  form.addEventListener('submit', () => {
    const submit = form.querySelector<HTMLButtonElement | HTMLInputElement>(
      '[type="submit"]'
    )
    if (!submit) return

    submit.disabled = true
    if (submit instanceof HTMLInputElement) submit.value = 'Отправляем…'
    else submit.textContent = 'Отправляем…'
  })

  refresh()
}

// Общие кнопки «Поделиться»: системное меню (Web Share API), текст лежит в
// data-share-text. Без API кнопка остаётся скрытой — рядом есть «Скопировать».
export function initShareButtons() {
  if (typeof navigator.share !== 'function') return

  document
    .querySelectorAll<HTMLButtonElement>('button[data-share-text]')
    .forEach((button) => {
      button.hidden = false
      button.addEventListener('click', () => {
        const text = button.dataset.shareText
        // Закрытое без выбора меню — тоже отказ промиса, это не ошибка.
        if (text) navigator.share({ text }).catch(() => {})
      })
    })
}

// Фильтр броней по квестам (lk/orders/_filters): дропдаун сайта в форму не
// пишет, поэтому выбранные квесты держим в скрытых полях quest_ids[]. В кнопке
// вместо «Все квесты» показываем названия выбранных.
function initQuestFilter() {
  const field = document.querySelector<HTMLElement>('[data-lk-quest-filter]')
  const title = field?.querySelector<HTMLElement>(
    '[data-dropdown-trigger-content]'
  )
  if (!field || !title) return

  const allLabel = title.textContent?.trim() ?? ''
  const options = Array.from(
    field.querySelectorAll<HTMLElement>('[data-dropdown-option]')
  )

  const sync = (ids: string[]) => {
    field
      .querySelectorAll('input[name="quest_ids[]"]')
      .forEach((input) => input.remove())
    ids.forEach((id) => {
      const input = document.createElement('input')
      input.type = 'hidden'
      input.name = 'quest_ids[]'
      input.value = id
      field.append(input)
    })
    const names = options
      .filter((option) => ids.includes(option.dataset.dropdownValue ?? ''))
      .map((option) => option.dataset.dropdownLabel)
    title.textContent = names.join(', ') || allLabel
  }

  field.addEventListener('dropdown-change', (event) => {
    const { value } = (event as CustomEvent<{ value: Array<string | number> }>)
      .detail
    sync(value.map(String))
  })
  sync(
    Array.from(
      field.querySelectorAll<HTMLInputElement>('input[name="quest_ids[]"]'),
      (input) => input.value
    )
  )
}

// Список броней в ЛК партнёра: посещение, сумма на месте и комментарий
// правятся прямо в строке и сохраняются сразу; номер брони открывает карточку
// в модальном окне. Карточка-страница остаётся для прямых ссылок и окон оплаты.
export function initLkOrdersPage() {
  initQuestFilter()
  initVisitReasonForm(document)

  const table = document.querySelector<HTMLElement>('[data-lk-orders-table]')
  if (!table) return

  table.addEventListener('change', (event) =>
    onRowChange(event.target as HTMLElement)
  )
  initVisitPopovers(table)
  initHints()
  initOrderModal(table)
}

// Форма брони: поле «Причина непосещения» показываем только при «Не был».
function initVisitReasonForm(root: ParentNode) {
  const visitSelect = root.querySelector<HTMLSelectElement>(
    'select[data-lk-visit-select]'
  )
  const reasonField = root.querySelector<HTMLElement>('[data-lk-reason-field]')
  if (!visitSelect || !reasonField) return

  const sync = () => {
    reasonField.hidden = visitSelect.value !== NOT_VISITED
  }
  visitSelect.addEventListener('change', sync)
  sync()
}

type OrderControl = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement

type OrderPatchResponse = {
  held?: boolean
  pending?: string | null
  notice?: string
  row_html?: string
  errors?: string[]
}

function onRowChange(target: HTMLElement) {
  const row = target.closest<HTMLElement>('[data-lk-order-row]')
  const field = target.dataset.lkOrderField
  if (!row || !field) return

  const control = target as OrderControl
  saveRow(row, { [field]: control.value }, [control])
}

// Посещение в строке: иконка отметки и кнопка «Редактировать», выбор — в
// маленьком окне под кнопкой. После сохранения ячейку перерисовывает сервер:
// отложенная на проверку правка оставляет прежнюю отметку.
function initVisitPopovers(table: HTMLElement) {
  let open: { cell: HTMLElement; popover: HTMLElement } | null = null

  const close = (restoreFocus: boolean) => {
    if (!open) return

    const { cell, popover } = open
    open = null
    popover.hidden = true
    const button = cell.querySelector<HTMLButtonElement>('[data-lk-visit-edit]')
    button?.setAttribute('aria-expanded', 'false')
    if (restoreFocus) button?.focus()
  }

  const show = (cell: HTMLElement) => {
    const popover = cell.querySelector<HTMLElement>('[data-lk-visit-popover]')
    if (!popover) return

    close(false)
    resetVisitPopover(popover)
    popover.hidden = false
    cell
      .querySelector('[data-lk-visit-edit]')
      ?.setAttribute('aria-expanded', 'true')
    open = { cell, popover }
    popover
      .querySelector<HTMLInputElement>('[data-lk-visit-option]:checked')
      ?.focus()
  }

  const save = (cell: HTMLElement, popover: HTMLElement) => {
    const row = cell.closest<HTMLElement>('[data-lk-order-row]')
    const visit = popover.querySelector<HTMLInputElement>(
      '[data-lk-visit-option]:checked'
    )
    const reason = popover.querySelector<HTMLSelectElement>(
      '[data-lk-visit-reason]'
    )
    if (!row || !visit || !reason) return

    const notVisited = visit.value === NOT_VISITED
    // «Не был» без причины сервер не примет.
    if (notVisited && !reason.value) {
      showVisitError(popover, 'Выберите причину непосещения')
      reason.focus()
      return
    }

    close(false)
    saveRow(
      row,
      {
        [VISIT_FIELD]: visit.value,
        [REASON_FIELD]: notVisited ? reason.value : ''
      },
      [],
      (data) => {
        const fresh = data.row_html && visitCellFrom(data.row_html)
        if (!fresh || !cell.isConnected) return
        cell.replaceWith(fresh)
        fresh.querySelector<HTMLButtonElement>('[data-lk-visit-edit]')?.focus()
      }
    )
  }

  table.addEventListener('click', (event) => {
    const target = event.target as HTMLElement
    const cell = target.closest<HTMLElement>('[data-lk-visit-cell]')

    if (open && cell !== open.cell) close(false)
    if (!cell) return

    if (target.closest('[data-lk-visit-edit]')) {
      if (open?.cell === cell) close(true)
      else show(cell)
    } else if (open && target.closest('[data-lk-visit-cancel]')) {
      close(true)
    } else if (open && target.closest('[data-lk-visit-save]')) {
      save(cell, open.popover)
    }
  })

  table.addEventListener('change', (event) => {
    const option = (event.target as HTMLElement).closest<HTMLInputElement>(
      '[data-lk-visit-option]'
    )
    const popover = option?.closest<HTMLElement>('[data-lk-visit-popover]')
    if (!option || !popover) return

    showVisitError(popover, '')
    popover.querySelector<HTMLSelectElement>('[data-lk-visit-reason]')!.hidden =
      option.value !== NOT_VISITED
  })

  document.addEventListener('click', (event) => {
    if (open && !open.cell.contains(event.target as Node)) close(false)
  })

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && open) close(true)
  })
}

// Окно открывается с сохранёнными значениями, а не с брошенным черновиком.
function resetVisitPopover(popover: HTMLElement) {
  let checked = ''
  popover
    .querySelectorAll<HTMLInputElement>('[data-lk-visit-option]')
    .forEach((option) => {
      option.checked = option.defaultChecked
      if (option.checked) checked = option.value
    })
  const reason = popover.querySelector<HTMLSelectElement>(
    '[data-lk-visit-reason]'
  )
  if (reason) {
    restoreControl(reason)
    reason.hidden = checked !== NOT_VISITED
  }
  showVisitError(popover, '')
}

function showVisitError(popover: HTMLElement, text: string) {
  const error = popover.querySelector<HTMLElement>('[data-lk-visit-error]')
  if (error) error.textContent = text
}

function visitCellFrom(rowHtml: string) {
  const template = document.createElement('template')
  template.innerHTML = rowHtml
  return template.content.querySelector<HTMLElement>('[data-lk-visit-cell]')
}

// Сохранения одной строки идут по очереди: иначе ответ на раннюю правку может
// прийти позже и показать устаревшее состояние заявки на проверку.
const rowQueues = new WeakMap<HTMLElement, Promise<void>>()

function saveRow(
  row: HTMLElement,
  payload: Record<string, string>,
  controls: OrderControl[],
  onSaved?: (_response: OrderPatchResponse) => void
) {
  const queued = (rowQueues.get(row) ?? Promise.resolve()).then(() =>
    sendRow(row, payload, controls, onSaved)
  )
  rowQueues.set(row, queued)
}

async function sendRow(
  row: HTMLElement,
  payload: Record<string, string>,
  controls: OrderControl[],
  onSaved?: (_response: OrderPatchResponse) => void
) {
  const url = row.dataset.lkOrderUrl
  if (!url) return

  setRowStatus(row, 'Сохранение…')
  controls.forEach((control) => (control.disabled = true))

  try {
    const response = await fetch(url, {
      method: 'PATCH',
      headers: jsonHeaders(),
      body: JSON.stringify({ order: payload })
    })
    const data = (await response.json()) as OrderPatchResponse
    if (!response.ok)
      throw new Error(data.errors?.join(', ') || 'Не удалось сохранить')

    setPending(row, data.pending ?? null)
    syncPayedPending(row, data.row_html)
    if (data.held) {
      // Спорную часть правки сервер отложил до проверки: в поле остаётся
      // запрошенное значение, прежнее — в строке «на проверке».
      controls.forEach(commitControl)
      setRowStatus(row, 'Отправлено на проверку Клаустрофобии')
    } else {
      controls.forEach(commitControl)
      setRowStatus(row, 'Сохранено', SAVED_STATUS_TIMEOUT)
    }
    onSaved?.(data)
  } catch (error) {
    controls.forEach(restoreControl)
    setRowStatus(
      row,
      error instanceof Error ? error.message : 'Не удалось сохранить'
    )
  } finally {
    controls.forEach((control) => (control.disabled = false))
  }
}

// Полная стоимость и строка «на проверке» под ней — из перерисованной сервером
// строки: в поле то, что записано в брони или в ждущей заявке. Поле, в котором
// площадка уже набирает следующую сумму, не трогаем.
function syncPayedPending(row: HTMLElement, rowHtml?: string) {
  if (!rowHtml) return

  const template = document.createElement('template')
  template.innerHTML = rowHtml
  const current = row.querySelector<HTMLElement>('[data-lk-payed-pending]')
  const fresh = template.content.querySelector<HTMLElement>(
    '[data-lk-payed-pending]'
  )
  if (current && fresh) current.replaceWith(fresh)

  const selector = '[data-lk-order-field="payed_owner_amount"]'
  const input = row.querySelector<HTMLInputElement>(selector)
  const saved = template.content.querySelector<HTMLInputElement>(selector)
  if (!input || !saved || input === document.activeElement) return

  input.defaultValue = saved.defaultValue
  input.value = saved.defaultValue
}

// Сохранённое значение поля — его значение по умолчанию: к нему откатываемся
// при ошибке.
function commitControl(control: OrderControl) {
  if (control instanceof HTMLSelectElement) {
    Array.from(control.options).forEach((option) => {
      option.defaultSelected = option.selected
    })
  } else {
    control.defaultValue = control.value
  }
}

function restoreControl(control: OrderControl) {
  if (control instanceof HTMLSelectElement) {
    const saved = Array.from(control.options).find(
      (option) => option.defaultSelected
    )
    control.value = saved?.value ?? ''
  } else {
    control.value = control.defaultValue
  }
}

function setPending(row: HTMLElement, summary: string | null) {
  const block = row.querySelector<HTMLElement>('[data-lk-order-pending]')
  const text = row.querySelector<HTMLElement>('[data-lk-order-pending-text]')
  if (!block || !text) return

  block.hidden = !summary
  text.textContent = summary ?? ''
}

// Подсказка «как посчитана комиссия»: на десктопе по наведению (CSS), на
// телефоне — по нажатию; открыта одна, закрывается кликом мимо и Esc.
function initHints() {
  let open: HTMLElement | null = null

  const toggle = (hint: HTMLElement | null) => {
    if (open && open !== hint) setHint(open, false)
    open = hint && open !== hint ? hint : null
    if (open) setHint(open, true)
    else if (hint) setHint(hint, false)
  }

  // Подсказки есть и в таблице, и в карточке брони в модальном окне.
  document.addEventListener('click', (event) => {
    const trigger = (event.target as HTMLElement).closest(
      '[data-lk-hint-trigger]'
    )
    if (trigger) toggle(trigger.closest<HTMLElement>('[data-lk-hint]'))
    else if (open) toggle(null)
  })
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && open) toggle(null)
  })
}

function setHint(hint: HTMLElement, visible: boolean) {
  hint.classList.toggle('lk-hint_open', visible)
  hint
    .querySelector('[data-lk-hint-trigger]')
    ?.setAttribute('aria-expanded', String(visible))
}

function setRowStatus(row: HTMLElement, text: string, clearAfter?: number) {
  const status = row.querySelector<HTMLElement>('[data-lk-order-status]')
  if (!status) return

  status.textContent = text
  if (clearAfter) {
    window.setTimeout(() => {
      if (status.textContent === text) status.textContent = ''
    }, clearAfter)
  }
}

function csrfToken() {
  return (
    document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')
      ?.content ?? ''
  )
}

function jsonHeaders(): Record<string, string> {
  return {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-CSRF-Token': csrfToken()
  }
}

// Карточка брони в модальном окне: та же разметка, что на странице брони
// (lk/orders/_details), форма сохраняется без перехода, строка списка
// обновляется по ответу сервера. Пока окно открыто, в адресной строке —
// адрес страницы брони: его партнёр пересылает КЦ, «Назад» закрывает окно.
function initOrderModal(table: HTMLElement) {
  const body = document.querySelector<HTMLElement>('[data-lk-order-modal-body]')
  const title = document.querySelector<HTMLElement>(
    '[data-lk-order-modal-title]'
  )
  const copyLink = document.querySelector<HTMLElement>(
    '[data-modal-name="lk_order_details"] [data-lk-order-copy-link]'
  )
  if (!body) return

  const modals = Modals.getInstance()
  // Номер открытия окна: ответ по брони, которую уже закрыли, в окно не попадает.
  let current = 0
  // Сохранённую строку меняем при закрытии окна — тогда фокус вернётся на неё.
  let updatedRow: { url: string; html: string } | null = null

  modals.addModal(
    ORDER_MODAL,
    {
      eventTimeout: 300,
      closeCallback: () => {
        current += 1
        // Окно закрыли крестиком, фоном или Esc — возвращаем адрес списка.
        if (orderInHistory()) history.back()
        if (!updatedRow) return

        const { url, html } = updatedRow
        updatedRow = null
        const row = findRow(table, url)
        if (!row) return
        row.outerHTML = html
        findRow(table, url)
          ?.querySelector<HTMLElement>('a[data-lk-order-open]')
          ?.focus()
      }
    },
    true
  )

  const load = async (url: string, notice?: string) => {
    const opening = current
    let html: string
    try {
      const response = await fetch(`${url}?modal=1`, {
        headers: { Accept: 'text/html' }
      })
      if (!response.ok) throw new Error(String(response.status))
      html = await response.text()
    } catch {
      if (opening === current) {
        body.textContent =
          'Не удалось загрузить бронь. Откройте её отдельной страницей.'
      }
      return
    }
    if (opening !== current) return

    body.innerHTML = html
    if (notice) {
      const flash = document.createElement('div')
      flash.className = 'lk-flash lk-flash_notice'
      flash.setAttribute('role', 'status')
      flash.textContent = notice
      body.prepend(flash)
    }
    initVisitReasonForm(body)
    body
      .querySelector<HTMLFormElement>('form[data-lk-order-form]')
      ?.addEventListener('submit', (event) => submit(event, url))
    refreshFocusLock()
  }

  const submit = async (event: SubmitEvent, url: string) => {
    event.preventDefault()
    const form = event.currentTarget as HTMLFormElement
    const button = form.querySelector<HTMLInputElement>('[type="submit"]')
    if (button) button.disabled = true

    try {
      const response = await fetch(form.action, {
        method: 'POST',
        body: new FormData(form),
        headers: { 'Accept': 'application/json', 'X-CSRF-Token': csrfToken() }
      })
      const data = (await response.json()) as OrderPatchResponse
      if (!response.ok) {
        showFormErrors(form, data.errors ?? ['Не удалось сохранить'])
        return
      }

      if (data.row_html) updatedRow = { url, html: data.row_html }
      await load(url, data.notice)
    } catch {
      showFormErrors(form, ['Не удалось сохранить'])
    } finally {
      if (button) button.disabled = false
    }
  }

  const openOrder = (url: string, link?: HTMLAnchorElement | null) => {
    current += 1
    if (title)
      title.textContent = `Бронирование №${link?.textContent?.trim() ?? ''}`
    if (copyLink)
      copyLink.dataset.copyText = new URL(url, window.location.origin).href
    body.textContent = 'Загрузка…'
    modals.openModal(ORDER_MODAL)
    load(url)
  }

  table.addEventListener('click', (event) => {
    const link = (event.target as HTMLElement).closest<HTMLAnchorElement>(
      'a[data-lk-order-open]'
    )
    // Ctrl/Cmd/Shift-клик — как обычная ссылка: страница брони в новой вкладке.
    if (
      !link ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey
    )
      return

    event.preventDefault()
    const url = new URL(link.href).pathname
    openOrder(url, link)
    history.pushState({ lkOrder: url }, '', url)
  })

  // «Назад» закрывает окно, «Вперёд» открывает его снова.
  window.addEventListener('popstate', () => {
    const url = orderInHistory()
    if (!url) {
      modals.closeModal(ORDER_MODAL)
      return
    }
    openOrder(
      url,
      findRow(table, url)?.querySelector<HTMLAnchorElement>(
        'a[data-lk-order-open]'
      )
    )
  })
}

// Бронь, открытая в окне, — по записи истории, которую окно добавило.
function orderInHistory() {
  return (history.state as { lkOrder?: string } | null)?.lkOrder
}

function findRow(table: HTMLElement, url: string) {
  return Array.from(
    table.querySelectorAll<HTMLElement>('[data-lk-order-row]')
  ).find((row) => row.dataset.lkOrderUrl === url)
}

function showFormErrors(form: HTMLFormElement, errors: string[]) {
  let box = form.querySelector<HTMLElement>('.lk-form__errors')
  if (!box) {
    box = document.createElement('div')
    box.className = 'lk-form__errors'
    form.prepend(box)
  }
  box.setAttribute('role', 'alert')
  box.tabIndex = -1
  box.textContent = errors.join(', ')
  box.focus()
}

// Пополнение баланса: комиссия шлюза удерживается из платежа, поэтому партнёр
// должен видеть, сколько дойдёт до баланса, ещё до оплаты.
export function initInvoicePreview() {
  const preview = document.querySelector<HTMLElement>('[data-invoice-preview]')
  const amountInput = document.querySelector<HTMLInputElement>(
    '[data-invoice-amount]'
  )
  if (!preview || !amountInput) return

  const unit = preview.dataset.unit ?? ''
  let rates: Record<string, number> = {}
  try {
    rates = JSON.parse(preview.dataset.feeRates ?? '{}')
  } catch {
    return
  }

  const money = (value: number) => `${value.toLocaleString('ru-RU')} ${unit}`
  const feeRow = preview.querySelector<HTMLElement>('[data-preview-fee-row]')
  const feeLabel = preview.querySelector<HTMLElement>(
    '[data-preview-fee-label]'
  )
  const charged = preview.querySelector<HTMLElement>('[data-preview-charged]')
  const fee = preview.querySelector<HTMLElement>('[data-preview-fee]')
  const credited = preview.querySelector<HTMLElement>('[data-preview-credited]')

  const selectedType = () =>
    document.querySelector<HTMLInputElement>(
      '[data-invoice-type-option]:checked'
    )?.value ?? ''

  const sync = () => {
    const amount = Math.floor(Number(amountInput.value))
    const rate = rates[selectedType()] ?? 0
    if (!Number.isFinite(amount) || amount <= 0) {
      preview.hidden = true
      return
    }

    // Округление вверх и «рубль обязан дойти» — как на сервере
    // (Invoice#payment_fee_amount).
    const feeValue = Math.min(Math.ceil(amount * rate), amount - 1)
    preview.hidden = false
    if (feeRow) feeRow.hidden = feeValue <= 0
    // Проценты пишем по-русски: запятая и без пробела перед знаком — как в карточке счёта.
    const percent = (rate * 100)
      .toFixed(2)
      .replace(/(\.\d*?)0+$/, '$1')
      .replace(/\.$/, '')
      .replace('.', ',')
    if (feeLabel) feeLabel.textContent = `Комиссия эквайринга ${percent}%`
    if (charged) charged.textContent = money(amount)
    if (fee) fee.textContent = `−${money(feeValue)}`
    if (credited) credited.textContent = money(amount - feeValue)
  }

  amountInput.addEventListener('input', sync)
  document
    .querySelectorAll<HTMLInputElement>('[data-invoice-type-option]')
    .forEach((option) => option.addEventListener('change', sync))
  sync()
}
